import { ArrowLeft, ArrowRight, Trash } from 'lucide-react';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { addCertification } from '../../redux/Slices/ResumeSlice';
import { deleteCertification } from '../../redux/Slices/ResumeSlice';
import Button from '../Common/Button';

type Props = {
  step: number;
  totalSteps: number;
  stepName: string;
  prevStepName: string;
  onNext: () => void;
  onBack: () => void;
};

type Certification = {
  name: string;
  issuingOrganization: string;
  issueDate: string;
  expiryDate: string;
  credentialId: string;
  credentialUrl: string;
  description: string;
};

const CertificationsForm: React.FC<Props> = ({
  step,
  totalSteps,
  stepName,
  prevStepName,
  onNext,
  onBack
}) => {
  const dispatch = useDispatch();

  const certifications: Certification[] = useSelector(
    (state: any) => state.root.resume.certifications
  );

  const NAME_LIMIT = 50;
  const ORG_LIMIT = 50;
  const ID_LIMIT = 50;
  const DESC_LIMIT = 200;

  const [form, setForm] = useState({
    name: '',
    issuingOrganization: '',
    issueDate: '',
    expiryDate: '',
    credentialId: '',
    credentialUrl: '',
    description: ''
  });

  const [errors, setErrors] = useState({
    name: '',
    issuingOrganization: '',
    issueDate: '',
    expiryDate: '',
    credentialId: '',
    credentialUrl: '',
    description: ''
  });

  /* changed to normal function */
  function CharCount(value: string, max: number) {
    const len = value?.length || 0;

    return (
      <span
        className={`text-xs ${len >= max ? 'text-red-500' : 'text-gray-400'}`}
      >
        {len}/{max}
      </span>
    );
  }

  const isValidUrl = (value: string) => {
    const pattern = /^(https?:\/\/)?([\w\d-]+\.)+[\w-]{2,}(\/.*)?$/i;

    return pattern.test(value);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    if (name === 'name' && value.length > NAME_LIMIT) return;
    if (name === 'issuingOrganization' && value.length > ORG_LIMIT) return;
    if (name === 'credentialId' && value.length > ID_LIMIT) return;
    if (name === 'description' && value.length > DESC_LIMIT) return;

    let errorMessage = '';

    if (name === 'credentialUrl' && value) {
      if (!isValidUrl(value)) {
        errorMessage = 'Enter valid URL';
      }
    }

    if (name === 'name' && value.length === NAME_LIMIT) {
      errorMessage = 'Max 30 characters reached';
    }

    if (name === 'issuingOrganization' && value.length === ORG_LIMIT) {
      errorMessage = 'Max 30 characters reached';
    }

    if (name === 'credentialId' && value.length === ID_LIMIT) {
      errorMessage = 'Max 50 characters reached';
    }

    if (name === 'description' && value.length === DESC_LIMIT) {
      errorMessage = 'Max 100 characters reached';
    }

    setErrors(prev => ({
      ...prev,
      [name]: errorMessage
    }));

    const dateFields = ['issueDate', 'expiryDate'];

    const formattedValue =
      dateFields.includes(name) && value
        ? new Date(value).toISOString()
        : value;

    setForm({
      ...form,
      [name]: formattedValue
    });
  };

  const isSaveValid =
    form.name.trim() !== '' && form.issuingOrganization.trim() !== '';

  const saveCertification = () => {
    if (!isSaveValid) return;
    dispatch(addCertification(form));

    setForm({
      name: '',
      issuingOrganization: '',
      issueDate: '',
      expiryDate: '',
      credentialId: '',
      credentialUrl: '',
      description: ''
    });
  };

  return (
    <div className='w-full bg-gamma'>
      <div className='flex-1 p-10'>
        <div className='mb-4 flex justify-between'>
          <div>
            <p className='text-sm text-gray-500'>
              Step {step} of {totalSteps}
            </p>

            <h1 className='text-2xl font-semibold'>{stepName}</h1>

            <p className='mt-1 text-sm text-gray-400'>
              Demonstrate your expertise
            </p>
          </div>

          <div className='text-brand'>
            {Math.round(((step - 1) / totalSteps) * 100)} %
          </div>
        </div>

        <div className='mb-8 h-1 rounded bg-gray-200'>
          <div
            className='h-1 rounded bg-brand'
            style={{
              width: `${((step - 1) / totalSteps) * 100}%`
            }}
          />
        </div>

        <div className='rounded-xl bg-white p-6 shadow-sm'>
          <h3 className='mb-4 font-semibold'>+ Add New Certification</h3>

          <div className='grid grid-cols-2 gap-4'>
            <div>
              <div className='flex justify-between'>
                <label className='text-sm font-medium'>
                  Certification Name <span className='text-red-500'>*</span>
                </label>

                {CharCount(form.name, NAME_LIMIT)}
              </div>

              <input
                name='name'
                maxLength={NAME_LIMIT}
                value={form.name}
                onChange={handleChange}
                placeholder='Enter certification'
                className={`mt-1 w-full rounded-lg border p-3 focus:outline-none
                ${errors.name ? 'border-red-500' : 'border-gray-300'}
                `}
              />
            </div>

            <div>
              <div className='flex justify-between'>
                <label className='text-sm font-medium'>
                  Issuing Organization <span className='text-red-500'>*</span>
                </label>

                {CharCount(form.issuingOrganization, ORG_LIMIT)}
              </div>

              <input
                name='issuingOrganization'
                maxLength={ORG_LIMIT}
                value={form.issuingOrganization}
                onChange={handleChange}
                placeholder='Issuing organization'
                className={`mt-1 w-full rounded-lg border p-3 focus:outline-none
                ${errors.issuingOrganization ? 'border-red-500' : 'border-gray-300'}
                `}
              />
            </div>
          </div>

          <div className='mt-4 grid grid-cols-2 gap-4'>
            <div>
              <label className='text-sm font-medium'>Issue Date</label>

              <input
                type='date'
                name='issueDate'
                value={form.issueDate?.split('T')[0] || ''}
                onChange={handleChange}
                className={`mt-1 w-full rounded-lg border p-3 focus:outline-none
                ${errors.issueDate ? 'border-red-500' : 'border-gray-300'}
                `}
              />
            </div>

            <div>
              <label className='text-sm font-medium'>Expiry Date</label>

              <input
                type='date'
                name='expiryDate'
                value={form.expiryDate?.split('T')[0] || ''}
                onChange={handleChange}
                className={`mt-1 w-full rounded-lg border p-3 focus:outline-none
                ${errors.expiryDate ? 'border-red-500' : 'border-gray-300'}
                `}
              />
            </div>
          </div>

          <div className='mt-4 grid grid-cols-2 gap-4'>
            <div>
              <div className='flex justify-between'>
                <label className='text-sm font-medium'>Credential ID</label>

                {CharCount(form.credentialId, ID_LIMIT)}
              </div>

              <input
                name='credentialId'
                maxLength={ID_LIMIT}
                value={form.credentialId}
                onChange={handleChange}
                placeholder='Enter credential ID'
                className={`mt-1 w-full rounded-lg border p-3 focus:outline-none
                ${errors.credentialId ? 'border-red-500' : 'border-gray-300'}
                `}
              />
            </div>

            <div>
              <label className='text-sm font-medium'>Credential URL</label>

              <input
                name='credentialUrl'
                value={form.credentialUrl}
                onChange={handleChange}
                placeholder='Enter credential URL'
                className={`mt-1 w-full rounded-lg border p-3 focus:outline-none
                ${errors.credentialUrl ? 'border-red-500' : 'border-gray-300'}
                `}
              />

              {errors.credentialUrl && (
                <p className='mt-1 text-xs text-red-500'>
                  {errors.credentialUrl}
                </p>
              )}
            </div>
          </div>

          <div className='mt-4'>
            <div className='flex justify-between'>
              <label className='text-sm font-medium'>Description</label>

              {CharCount(form.description, DESC_LIMIT)}
            </div>

            <textarea
              name='description'
              maxLength={DESC_LIMIT}
              value={form.description}
              onChange={handleChange}
              rows={3}
              placeholder='Enter description'
              className={`mt-1 w-full rounded-lg border p-3 focus:outline-none
              ${errors.description ? 'border-red-500' : 'border-gray-300'}
              `}
            />
          </div>

          <Button
            label='+ Save Certification'
            handleClickBtn={saveCertification}
            variant='contained'
            disabled={!isSaveValid}
            classes='mt-5'
          />
        </div>

        <div className='mt-6 rounded-xl bg-white p-6 shadow-sm'>
          <h3 className='mb-4 font-semibold'>Added Certifications</h3>

          {certifications.length === 0 ? (
            <p className='text-sm text-gray-400'>
              No certifications added yet.
            </p>
          ) : (
            certifications.map((cert: Certification) => (
              <div
                key={cert.name}
                className='mb-2 flex items-center justify-between rounded-lg border p-3'
              >
                <div>
                  <p className='font-medium'>{cert.name}</p>

                  <p className='text-xs text-gray-500'>
                    {cert.issuingOrganization}
                  </p>
                </div>

                <button
                  onClick={() => dispatch(deleteCertification(cert.name))}
                  className='text-red-500'
                >
                  <Trash size={16} />
                </button>
              </div>
            ))
          )}
        </div>

        <div className='mt-10 flex items-center justify-between'>
          <Button
            label={`Previous: ${prevStepName}`}
            handleClickBtn={onBack}
            variant='outlined'
          >
            <ArrowLeft size={16} />
          </Button>

          <Button
            label='Continue'
            handleClickBtn={onNext}
            variant='contained'
            iconPosition='right'
          >
            <ArrowRight size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CertificationsForm;
